/**
 * Route-Registry — 17 echte Apps-Script-Views + Hub (Phase 3.5 §5).
 *
 * Mapping zu `cotrainer-apps-script/app/*_view.html`:
 *   /                       → hub.ts (entspricht hub_view.html)
 *   /caller                 → caller_overview_view.html
 *   /caller/bilanz          → caller_bilanz_view.html
 *   /caller/pflege          → caller_pflege_view.html
 *   /meetings               → meetings_overview_view.html
 *   /meetings/kalender      → meetings_kalender_view.html
 *   /meetings/slots         → meetings_slots_view.html
 *   /fu                     → fu_overview_view.html
 *   /fu/kalender            → fu_kalender_view.html
 *   /funnel                 → funnel_status_view.html (default)
 *   /funnel/status          → funnel_status_view.html
 *   /funnel/performance     → funnel_performance_view.html
 *   /closings               → closings_overview_view.html
 *   /closings/liste         → closings_liste_view.html
 *   /pflegeluecken          → pflegeluecken_view.html
 *   /ziele                  → ziele_view.html
 *   /uebersicht             → vertrieb_uebersicht_view.html
 *   /dq                     → dq_view.html
 *
 * Stub-Routes liefern eine "Coming §5"-Karte mit Endpoint-Hint. Wenn eine
 * View portiert ist, ersetze ich den Eintrag durch echten Import.
 */

import { registerRoute } from "./router";

export function registerAllRoutes(): void {
  registerRoute("/", () => import("../views/hub"));
  registerRoute("/hub", () => import("../views/hub"));

  // Mein Bereich (Phase 3.x) — Profil, Konto, Urlaub (Platzhalter), Stunden (Platzhalter)
  registerRoute("/profil", () => import("../views/profil"));
  registerRoute("/konto", () => import("../views/konto"));
  registerRoute("/urlaub", () => import("../views/urlaub"));
  registerRoute("/stunden", () => import("../views/stunden"));
  registerRoute("/krank", () => import("../views/krank"));

  // HR-Bereich (Phase 8) — nur HR-Leiter/Admin (Sichtbarkeit via bereiche.ts requiresBl)
  registerRoute("/hr", () => import("../views/hr-uebersicht"));
  registerRoute("/hr/urlaub", () => import("../views/hr-urlaub"));
  registerRoute("/hr/zeit", () => import("../views/hr-zeit"));
  registerRoute("/hr/einstellungen", () => import("../views/hr-einstellungen"));
  registerRoute("/hr/mitarbeiter", () => import("../views/hr-mitarbeiter"));
  registerRoute("/hr/mitarbeiter/:id", () => import("../views/hr-ma-detail"));
  registerRoute("/hr/abwesenheiten", () => import("../views/hr-abwesenheiten"));

  // Kundenbetreuung (KB) — Vereinsbetreuung (K1c) + Einstellungen (P0b)
  registerRoute("/kb", () => import("../views/kb-vereine"));
  registerRoute("/kb/dashboard", () => import("../views/kb-dashboard"));
  registerRoute("/kb/sonderaktionen", () => import("../views/kb-sonderaktionen"));
  registerRoute("/kb/verein/:id", () => import("../views/kb-verein-detail"));
  registerRoute("/kb/kontakte", () => import("../views/kb-kontakte"));
  registerRoute("/kb/erfassung", () => import("../views/kb-erfassung"));
  registerRoute("/kb/betreuung", () => import("../views/kb-betreuungsliste")); // P8
  registerRoute("/kb/aufgaben", () => import("../views/kb-aufgaben")); // Paket 3
  registerRoute("/kb/kalender", () => import("../views/kb-kalender")); // P11
  registerRoute("/kb/bestand", () => import("../views/kb-bestand")); // P13c
  registerRoute("/kb/einstellungen", () => import("../views/kb-einstellungen"));
  registerRoute("/kb/audit", () => import("../views/kb-audit"));

  // Finance (F0) — Fundament: Übersicht (Landing) + geteilte Taxonomie.
  registerRoute("/finance", () => import("../views/finance-uebersicht"));
  registerRoute("/finance/taxonomie", () => import("../views/finance-taxonomie"));
  // Finance (F1) — Finance Master in der App: Vereinsabo-Liste + Detail.
  registerRoute("/finance/vereinsabo", () => import("../views/finance-vereinsabo"));
  registerRoute("/finance/vereinsabo/:id", () => import("../views/finance-vereinsabo-detail"));
  // Finance (F2) — IST-Layer / Buchhaltung: Konten + kategorisiertes Kontobuch.
  registerRoute("/finance/konten", () => import("../views/finance-konten"));
  registerRoute("/finance/buchhaltung", () => import("../views/finance-buchhaltung"));
  // Finance (F3) — Business Case SOLL/PLAN + IST: Annahmen, Plan-Output, Soll-Ist.
  registerRoute("/finance/plan", () => import("../views/finance-plan-output"));
  registerRoute("/finance/plan/annahmen", () => import("../views/finance-plan-annahmen"));
  registerRoute("/finance/soll-ist", () => import("../views/finance-soll-ist"));
  // Finance (F4) — Ziele + Reporting / Gesellschafter-One-Pager.
  registerRoute("/finance/ziele", () => import("../views/finance-ziele"));
  registerRoute("/finance/reporting", () => import("../views/finance-reporting"));

  // Admin (Phase 3.7.2) — MA-Verwaltung
  registerRoute("/admin/ma", () => import("../views/admin-ma"));
  // Admin (Feature B) — Berechtigungs-Matrix (read-only Übersicht, Edit via MA-Modal)
  registerRoute("/admin/berechtigungen", () => import("../views/admin-berechtigungen"));
  registerRoute("/admin/zugriffsanfragen", () => import("../views/admin-zugriffsanfragen"));
  // Admin (Phase 3.7.4) — System-Settings
  registerRoute("/admin/settings", () => import("../views/admin-settings"));
  // Admin (Phase 3.7.5) — Sheet-Inventar
  registerRoute("/admin/sheets", () => import("../views/admin-sheets"));
  // Admin (Phase 3.7.6) — Audit-Log
  registerRoute("/admin/audit", () => import("../views/admin-audit"));
  // Admin (Phase 7.4) — System: Datumsformat
  registerRoute("/admin/system/datumsformat", () => import("../views/admin-system-format"));
  registerRoute("/admin/system/feiertage", () => import("../views/admin-feiertage"));
  registerRoute("/admin/system/companies-cache", () => import("../views/admin-companies-cache"));
  registerRoute("/admin/system/integrationen", () => import("../views/admin-integrations"));
  // Vertrieb → Einstellungen (Phase 7.4 + Nav-Umbau 2026-06-04):
  // Container-View mit Tab-Leiste (Zahnrad im Sub-Sidebar-Head). Die nackten
  // Einzel-View-Routen laufen jetzt alle über den Container, der die passende
  // Sub-View einbettet. /vertrieb/einstellungen (ohne Sub) → erster Tab.
  const vertriebEinstellungen = () => import("../views/vertrieb-einstellungen");
  registerRoute("/vertrieb/einstellungen", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/roster", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/slot-verfuegbarkeit", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/schwellen", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/slack-templates", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/referenzen", vertriebEinstellungen);
  registerRoute("/vertrieb/einstellungen/lost-grund", vertriebEinstellungen);

  // -----------------------------------------------------------------------
  // BATCH A — Worker-Endpoints aus Phase 3.2 vorhanden
  // -----------------------------------------------------------------------
  registerRoute("/pflegeluecken", () => import("../views/pflegeluecken"));
  registerRoute("/fu", () => import("../views/fu-overview"));
  registerRoute("/fu/kalender", () => import("../views/fu-kalender"));

  // -----------------------------------------------------------------------
  // BATCH B — Worker-Endpoints fehlen, kommen mit §5
  // -----------------------------------------------------------------------
  registerRoute("/caller", () => import("../views/caller-overview"));
  registerRoute("/caller/bilanz", () => import("../views/caller-bilanz"));
  registerRoute("/meetings", () => import("../views/meetings-overview"));
  registerRoute("/meetings/kalender", () => import("../views/meetings-kalender"));
  registerRoute("/closings", () => import("../views/closings-overview"));
  registerRoute("/closings/liste", () => import("../views/closings-liste"));
  registerRoute("/funnel", () => import("../views/funnel-status"));
  registerRoute("/funnel/status", () => import("../views/funnel-status"));
  registerRoute("/funnel/performance", () => import("../views/funnel-performance"));
  registerRoute("/uebersicht", () => import("../views/vertriebs-uebersicht"));
  registerRoute("/vertriebshistorie", () => import("../views/vertriebshistorie"));
  registerRoute("/vertriebshistorie/:companyId", () => import("../views/vertriebshistorie-detail"));
  registerRoute("/ziele", () => import("../views/ziele"));
  registerRoute("/dq", () => import("../views/dq"));

  // -----------------------------------------------------------------------
  // BATCH C — Write-Dialoge in Views (kein eigener Route)
  // -----------------------------------------------------------------------
  registerRoute("/meetings/slots", () => import("../views/meetings-slots"));
  registerRoute("/caller/pflege", () => import("../views/caller-pflege"));
}

